import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { QuizResult } from "@/components/QuizResult";
import { QUESTIONS, RESULTS, scoreQuiz, trackQuiz } from "@/lib/quiz";
import { useAuth } from "@/context/AuthContext";

function Question({ item, index, value, onPick }) {
  return (
    <div className="glass p-6 sm:p-8 fade-up" key={item.key} data-testid={`quiz-question-${item.key}`}>
      <p className="font-mono-num text-xs uppercase tracking-widest text-cyan-700 dark:text-cyan-400">Question {index + 1} of {QUESTIONS.length}</p>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-2">{item.q}</h2>
      <p className="text-sm text-muted-foreground mt-2">{item.hint}</p>
      <div className="grid gap-3 mt-6">
        {item.options.map((o) => (
          <button key={o.v} type="button" onClick={() => onPick(o.v)} data-testid={`quiz-option-${item.key}-${o.v}`}
            className={`text-left rounded-2xl border px-5 py-4 text-sm sm:text-base font-medium transition-colors duration-200 hover:border-cyan-500 hover:bg-cyan-50/70 dark:hover:bg-cyan-950/30 ${value === o.v ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-950/40" : "border-border bg-white/70 dark:bg-slate-900/60"}`}>
            {o.t}
          </button>
        ))}
      </div>
    </div>
  );
}

function Progress({ step }) {
  return (
    <div className="flex gap-2 mb-6" data-testid="quiz-progress">
      {QUESTIONS.map((q, i) => <div key={q.key} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-gradient-to-r from-emerald-500 to-cyan-500" : "bg-muted"}`} />)}
    </div>
  );
}

function FriendBanner({ from }) {
  if (!RESULTS[from]) return null;
  return (
    <div className="glass p-4 mb-6 flex items-center gap-3 text-sm fade-up" data-testid="quiz-friend-banner">
      <Sparkle size={18} weight="fill" className="text-cyan-600 shrink-0" />
      <p>A friend tried PillarWealth and said <span className="font-semibold">"{RESULTS[from].short}"</span>. Try the free app, then tell us what you think below.</p>
    </div>
  );
}

export default function RetirementCheck() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const done = step >= QUESTIONS.length;
  const from = params.get("from");
  useEffect(() => { if (RESULTS[from]) trackQuiz(from, "arrival"); }, [from]);
  const pick = (v) => {
    const next = { ...answers, [QUESTIONS[step].key]: v };
    setAnswers(next);
    if (step === QUESTIONS.length - 1) trackQuiz(scoreQuiz(next), "complete");
    setTimeout(() => setStep((s) => s + 1), 180);
  };
  const restart = () => { setAnswers({}); setStep(0); };
  return (
    <div className="max-w-3xl" data-testid="retirement-check-page">
      <Seo title="What Did PillarWealth Show You? 3-Question Quiz" description="Tried PillarWealth's free FERS pension, TSP and retirement-date tools? Answer 3 quick questions, see your result and share it with coworkers. Free and unlimited." path="/check" />
      <p className="text-xs uppercase tracking-widest text-cyan-700 dark:text-cyan-400">3-question quiz · free &amp; unlimited for everyone</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-grad-head mt-2 mb-3">What did PillarWealth show you?</h1>
      <p className="text-base text-muted-foreground mb-8">Try the app's pension, TSP and retirement-date tools, then answer 3 quick questions and share your take. No personal numbers are shared.{!user && <> New here? <Link to="/login?tab=register" className="text-cyan-700 dark:text-cyan-400 underline" data-testid="quiz-try-app-link">Try the app free</Link> first.</>}</p>
      <FriendBanner from={from} />
      {done ? <QuizResult result={scoreQuiz(answers)} onRestart={restart} /> : (
        <>
          <Progress step={step} />
          <Question item={QUESTIONS[step]} index={step} value={answers[QUESTIONS[step].key]} onPick={pick} />
          <div className="flex justify-between mt-4">
            <Button variant="ghost" className="rounded-full" disabled={!step} onClick={() => setStep((s) => s - 1)} data-testid="quiz-back-button"><ArrowLeft size={16} className="mr-1" />Back</Button>
            <Link to="/learn" className="text-sm text-muted-foreground self-center hover:underline" data-testid="quiz-learn-link">Browse guides instead <ArrowRight size={12} className="inline" /></Link>
          </div>
        </>
      )}
    </div>
  );
}
